import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import {
  CheckboxValue,
  FilterPill,
  FilterValues,
  SrpColorsFilterValue,
  SrpFilterName,
  SrpFilterRangeValue,
  SrpFilterValues,
  SrpTruckOptionsFilterValue,
} from 'types/srpFilters';
import useFilterPillCreators from 'components/Srp/FilterPills/hooks/useCreateFilterPills';
import useHashParamFeatureFlag from 'lib/hooks/useHashParamFeatureFlag';

function checkBoolean(filter: CheckboxValue): boolean {
  let booleanFlag = false;
  Object.keys(filter).forEach((key: string) => {
    if (filter[key]) {
      booleanFlag = true;
    }
  });
  return booleanFlag;
}

const defaultValueHandlers = {
  // Simple value filters
  [SrpFilterName.Condition]: (value: FilterValues): boolean =>
    value === srpFilterInitialValues[SrpFilterName.Condition],
  [SrpFilterName.Doors]: (value: FilterValues): boolean =>
    value === srpFilterInitialValues[SrpFilterName.Doors],
  [SrpFilterName.MakeModel]: (value: FilterValues): boolean =>
    value === srpFilterInitialValues[SrpFilterName.MakeModel],
  [SrpFilterName.Trims]: (value: FilterValues): boolean =>
    value === srpFilterInitialValues[SrpFilterName.Trims],
  [SrpFilterName.Category]: (value: FilterValues): boolean =>
    value === srpFilterInitialValues[SrpFilterName.Category],

  // Checkbox filters
  [SrpFilterName.Advanced]: (value: FilterValues): boolean => !checkBoolean(value as CheckboxValue),
  [SrpFilterName.Cylinders]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),
  [SrpFilterName.Transmission]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),
  [SrpFilterName.Drivetrain]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),
  [SrpFilterName.Powertrain]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),
  [SrpFilterName.BodyTypes]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),
  [SrpFilterName.Location]: (value: FilterValues): boolean => !checkBoolean(value as CheckboxValue),
  [SrpFilterName.FeaturesTechnology]: (value: FilterValues): boolean =>
    !checkBoolean(value as CheckboxValue),

  // Range filters
  [SrpFilterName.Year]: (value: FilterValues): boolean => {
    const { min, max } = value as SrpFilterRangeValue;
    const defaultRange = srpFilterInitialValues[SrpFilterName.Year];
    return min === defaultRange.min && max === defaultRange.max;
  },
  [SrpFilterName.Mileage]: (value: FilterValues): boolean => {
    const { min, max } = value as SrpFilterRangeValue;
    const defaultRange = srpFilterInitialValues[SrpFilterName.Mileage];
    return min === defaultRange.min && max === defaultRange.max;
  },
  [SrpFilterName.Price]: (value: FilterValues): boolean => {
    const { min, max } = value as SrpFilterRangeValue;
    const defaultRange = srpFilterInitialValues[SrpFilterName.Price];
    return min === defaultRange.min && max === defaultRange.max;
  },

  // Special cases
  [SrpFilterName.MultiMakeModel]: (value: FilterValues): boolean =>
    (value as SrpFilterValues[SrpFilterName.MultiMakeModel]).every(
      (item) => item.make === undefined && item.model === undefined
    ),
  [SrpFilterName.TruckOptions]: (value: FilterValues): boolean => {
    const { bedLength, bedType, cabinType, rearWheel } = value as SrpTruckOptionsFilterValue;
    return (
      !checkBoolean(bedType as unknown as CheckboxValue) &&
      !checkBoolean(rearWheel as unknown as CheckboxValue) &&
      !checkBoolean(cabinType as unknown as CheckboxValue) &&
      bedLength.min === 3 &&
      bedLength.max === 9
    );
  },
  [SrpFilterName.Colors]: (value: FilterValues): boolean => {
    const { exterior, interior } = value as SrpColorsFilterValue;
    return (
      !checkBoolean(exterior as unknown as CheckboxValue) &&
      !checkBoolean(interior as unknown as CheckboxValue)
    );
  },
  [SrpFilterName.Radius]: (value: FilterValues): boolean => {
    return Number(value) === srpFilterInitialValues[SrpFilterName.Radius];
  },
  [SrpFilterName.SortBy]: (value: FilterValues): boolean => {
    return !value;
  },
  [SrpFilterName.FuelEfficiency]: (value: FilterValues): boolean => {
    return (value as number) === 0;
  },
};

export default function useGetFilterPills(filters: SrpFilterValues): FilterPill[] {
  const multiMakeModelEnabled = useHashParamFeatureFlag('multiMakeModel');
  const filterPillFunctions = useFilterPillCreators();

  const filterPills = Object.entries(filters)
    // First filter out default values and MakeModel when multiMakeModelEnabled
    .filter(
      ([filterName, filterValues]) =>
        !((defaultValueHandlers as any)[filterName]?.(filterValues) ?? true) &&
        !(multiMakeModelEnabled && filterName === 'MakeModel')
    )
    // Then process only the non-default filters
    .flatMap(([filterName, filterValues]) => {
      const createFilterPillFunc = filterPillFunctions[filterName];
      if (!createFilterPillFunc) return [];

      const createdFilterPill = createFilterPillFunc(filterValues as never);
      return createdFilterPill || [];
    });

  return filterPills;
}
